import React, {
  useState,
  useRef,
  useEffect,
  Fragment,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Formik, FieldArray } from 'formik'
import { useParams } from 'react-router-dom'
import {
  ABSpinner,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import ExperienceCopyModal from './ExperienceCopyModal'
import QuestionCard from './QuestionCard'
import QuestionEditor from './QuestionEditor'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import QuestionCopyPreviewModal from './QuestionCopyPreviewModal'

const defaultValues = {
  questions: [],
  questCopy: null,
}

// Defined outside the editor so React keeps a stable component type.
// Nesting these inside QuestTypeformEditor remounted them every render and
// retriggered their effects → "Maximum update depth exceeded".
const FormResetWatcher = ({
  shouldResetForm,
  formData,
  resetForm,
  onResetDone,
}) => {
  useEffect(() => {
    if (shouldResetForm) {
      resetForm({ values: formData })
      onResetDone()
    }
  }, [shouldResetForm, formData, resetForm, onResetDone])

  return null
}

const QuestionsSyncWatcher = ({ questions, onQuestionsChange }) => {
  const onChangeRef = useRef(onQuestionsChange)
  onChangeRef.current = onQuestionsChange

  useEffect(() => {
    if (onChangeRef.current) {
      onChangeRef.current(Array.isArray(questions) ? questions : [])
    }
  }, [questions])

  return null
}

// Function to reconstruct addressFields from subQuestions
const reconstructAddressFields = (question) => {
  // If addressFields is provided in the response, use it
  if (question.addressFields) {
    return question.addressFields
  }

  // Otherwise, reconstruct addressFields from subQuestions
  if (question.answerType === 'object' && question.subQuestions) {
    const addressFields = {
      address: false,
      address2: false,
      city: false,
      state: false,
      zip: false,
      country: false,
    }

    // Map subQuestion names back to addressFields keys
    question.subQuestions.forEach((subQuestion) => {
      const subQuestionName = subQuestion.subQuestion
      if (subQuestionName === 'Address') addressFields.address = true
      else if (subQuestionName === 'Address2') addressFields.address2 = true
      else if (subQuestionName === 'City') addressFields.city = true
      else if (subQuestionName === 'State') addressFields.state = true
      else if (subQuestionName === 'Zip') addressFields.zip = true
      else if (subQuestionName === 'Country') addressFields.country = true
    })

    return addressFields
  }

  // Default fallback
  return {
    address: true,
    address2: true,
    city: true,
    state: true,
    zip: true,
    country: true,
  }
}

// Helper function to convert condition values to IDs
const convertConditionToIds = (condition, parentQuestion) => {
  if (
    !condition ||
    !Array.isArray(condition) ||
    !parentQuestion?.originalOptions
  ) {
    return condition
  }

  return condition.map((conditionValue) => {
    // If conditionValue is already an ID (not a string that matches any option value), return it as is
    const matchingOption = parentQuestion.originalOptions.find((opt) => {
      const optValue =
        typeof opt === 'object' && opt !== null ? opt.option : opt
      return optValue === conditionValue
    })

    if (
      matchingOption &&
      typeof matchingOption === 'object' &&
      matchingOption !== null
    ) {
      return matchingOption.id || matchingOption._id || conditionValue
    }

    return conditionValue
  })
}

// Function to create a fresh copy of initial question
const createFreshQuestion = () => ({
  text: '',
  description: '',
  answerType: 'text',
  questionType: 'direct',
  dependentQuestionId: '',
  dependentQuestion: '',
  condition: [],
  isRequired: false,
  imageFileName: '',
  position: 'left',
  connectedTo: '',
  columnNameforReport: '',
  originalOptions: [],
  subQuestions: [],
  addressFields: {
    address: true,
    address2: true,
    city: true,
    state: true,
    zip: true,
    country: true,
  },
  isUploading: false,
})

// Reusable function to fetch and process custom form data
const fetchAndProcessCustomForm = async (targetQuestId) => {
  try {
    const response = await API.ClientAPI.quests.getCustomForm(targetQuestId)
    const { data } = response

    if (!data || Object.keys(data).length === 0) {
      return {
        questions: [],
        questCopy: null,
        customFormId: null,
        formExists: false,
      }
    }

    // First pass: create all questions
    const questions = (data.questions || []).map((q) => {
      const questionData = {
        id: q._id || q.id,
        text: q.question || '',
        description: q.description || '',
        answerType: q.answerType || 'text',
        questionType: q.questionType || 'direct',
        dependentQuestionId: q.dependentQuestionId || '',
        dependentQuestion: '', // Will be populated in second pass
        condition: q.condition || [],
        isRequired: q.isRequired || false,
        originalOptions: Array.isArray(q.options) ? q.options : [],
        subQuestions: q.subQuestions || [],
        imageFileName: q.questionImageName || '',
        position: q.position || '',
        connectedTo: q.connectedTo || '',
        columnNameforReport: q.columnNameforReport || '',
        addressFields: reconstructAddressFields(q),
        isUploading: false,
        isDeleted: q.isDeleted || false,
      }

      // Only add scaleOptions for scale type questions
      if (q.answerType === 'scale') {
        questionData.scaleOptions = q.scaleOptions || {
          maxScaleLimit: 6,
          lowSubtitle: '',
          highSubtitle: '',
        }
      }

      return questionData
    })

    // Second pass: populate dependent question text for dependent questions
    const questionsWithDependentText = questions.map((q) => {
      if (q.questionType === 'dependent' && q.dependentQuestionId) {
        // Find the parent question to get its text
        const parentQuestion = questions.find(
          (parent) => (parent.id || parent._id) === q.dependentQuestionId,
        )
        if (parentQuestion) {
          q.dependentQuestion = parentQuestion.text
        }
      }
      return q
    })

    return {
      questions: questionsWithDependentText.filter((q) => !q.isDeleted),
      questCopy: null,
      customFormId: data._id || data.id,
      formExists: true,
    }
  } catch (e) {
    throw e
  }
}

const QuestTypeformEditor = forwardRef(
  (
    {
      onFormSaved,
      onQuestionsChange,
      onPreviewClick,
      isPreviewDisabled,
      hidePreviewButton = false,
    },
    ref,
  ) => {
    const { questId } = useParams()
    const Error = useErrorService()
    const Notification = useNotificationService()
    const imageRefs = useRef([])
    const [showCopyPicker, setShowCopyPicker] = useState(false)

    // Form state
    const [formState, setFormState] = useState({
      customFormId: null,
      formExists: false,
      isLoading: false,
      isFormSubmitting: false,
      shouldResetForm: false,
    })

    // Form data
    const [formData, setFormData] = useState(defaultValues)

    // Validation errors
    const [validationErrors, setValidationErrors] = useState({})

    // Modal state
    const [deleteModal, setDeleteModal] = useState({
      show: false,
      questionToDelete: null,
      dependentQuestions: [],
      isLoading: false,
    })

    const [deletingQuestionId, setDeletingQuestionId] = useState(null)

    // Inline question editor state (replaces modal)
    const [questionEditor, setQuestionEditor] = useState({
      open: false,
      question: null,
      questionIndex: null,
      saveMode: 'append',
      insertAfterIndex: null,
    })

    // Copy form loading state
    const [isCopyingForm, setIsCopyingForm] = useState(false)

    // Question copy preview modal state
    const [copyPreviewModal, setCopyPreviewModal] = useState({
      show: false,
      questions: [],
      selectedQuest: null,
      pendingCopyData: null,
    })

    // Copy operation state
    const [isCopyingOperation, setIsCopyingOperation] = useState(false)

    useImperativeHandle(ref, () => ({
      openCopyFrom: () => {
        if (!isCopyingForm) {
          setShowCopyPicker(true)
        }
      },
    }))

    // Helper function to build question payload
    const buildQuestionPayload = (q, questions, isCopying = false) => {
      const questionPayload = {
        id: q._id || q.id,
        question: q.text,
        ...(q.answerType && { answerType: q.answerType }),
        ...(q.questionType && { questionType: q.questionType }),
        ...(q.questionType === 'dependent' &&
          q.dependentQuestionId && {
            dependentQuestionId: q.dependentQuestionId,
            ...(isCopying && { dependentQuestion: q.dependentQuestion || '' }),
          }),
        ...(q.questionType === 'dependent' &&
          q.condition &&
          q.condition.length > 0 && {
            condition: (() => {
              // Find the parent question to convert condition values to IDs
              const parentQuestion = questions.find(
                (parent) => (parent.id || parent._id) === q.dependentQuestionId,
              )
              return convertConditionToIds(q.condition, parentQuestion)
            })(),
          }),
        ...(q.imageFileName &&
        q.imageFileName.trim &&
        q.imageFileName.trim() !== ''
          ? { questionImageName: q.imageFileName }
          : {}),
        ...(q.position && q.position.trim && q.position.trim() !== ''
          ? { position: q.position }
          : {}),
        ...(q.answerType === 'files' &&
        q.connectedTo &&
        q.connectedTo.trim &&
        q.connectedTo.trim() !== ''
          ? { connectedTo: q.connectedTo }
          : {}),
        ...(q.columnNameforReport &&
        q.columnNameforReport.trim &&
        q.columnNameforReport.trim() !== ''
          ? { columnNameforReport: q.columnNameforReport }
          : {}),
        ...(q.isRequired ? { isRequired: true } : {}),
        isDeleted: false,
      }

      if (q.description && q.description.trim()) {
        questionPayload.description = q.description.trim()
      }

      if (
        q.answerType === 'singleSelectionDropdown' ||
        q.answerType === 'multipleSelectionDropdown'
      ) {
        // Filter out empty options and map to proper structure
        questionPayload.options = (q.originalOptions || [])
          .filter((option) => {
            const optionText = option.option || option.text || ''
            return optionText.trim().length > 0
          })
          .map((option) => {
            const optionObj = {
              option: option.option || option.text || '',
            }

            // Include id if it exists, or convert _id to id if _id exists
            if (option.id) {
              optionObj.id = option.id
            } else if (option._id) {
              optionObj.id = option._id
            }

            return optionObj
          })
      }

      if (q.answerType === 'object') {
        const originalSubQuestions = q.subQuestions || []
        const enabledFields = Object.entries(q.addressFields || {})
          .filter(([_, enabled]) => enabled)
          .map(([field, _]) => field)

        questionPayload.subQuestions = enabledFields.map((field) => {
          const subQuestionName = field.charAt(0).toUpperCase() + field.slice(1)

          const existingSubQuestion = originalSubQuestions.find(
            (sub) => sub.subQuestion === subQuestionName,
          )

          return {
            id: existingSubQuestion?._id || existingSubQuestion?.id,
            subQuestion: subQuestionName,
          }
        })
      }

      if (q.answerType === 'scale') {
        const scaleOptions = {}
        const maxLimit = q.scaleOptions?.maxScaleLimit
        if (typeof maxLimit === 'number' && maxLimit >= 5 && maxLimit <= 10) {
          scaleOptions.maxScaleLimit = maxLimit
        }
        const low = q.scaleOptions?.lowSubtitle?.trim()
        if (low) scaleOptions.lowSubtitle = low
        const high = q.scaleOptions?.highSubtitle?.trim()
        if (high) scaleOptions.highSubtitle = high
        if (Object.keys(scaleOptions).length > 0) {
          questionPayload.scaleOptions = scaleOptions
        }
      }

      return questionPayload
    }

    // Initialize form data
    useEffect(() => {
      async function fetchCustomForm() {
        if (questId) {
          setFormState((prev) => ({ ...prev, isLoading: true }))
          try {
            const result = await fetchAndProcessCustomForm(questId)
            setFormData({
              questions: result.questions,
              questCopy: result.questCopy,
            })
            setFormState({
              customFormId: result.customFormId,
              formExists: result.formExists,
              isLoading: false,
            })
          } catch (e) {
            setFormData({ questions: [], questCopy: null })
            setFormState({
              customFormId: null,
              formExists: false,
              isLoading: false,
            })
          }
        } else {
          setFormData({ questions: [], questCopy: null })
          setFormState({
            customFormId: null,
            formExists: false,
            isLoading: false,
          })
        }
      }
      fetchCustomForm()
    }, [questId])

    // Function to find dependent questions
    const findDependentQuestions = (questionId, allQuestions) => {
      if (!questionId) return []

      return allQuestions
        .filter(
          (q) =>
            q.questionType === 'dependent' &&
            (q.dependentQuestionId === questionId ||
              q.dependentQuestionId ===
                (questionId.toString && questionId.toString())),
        )
        .map((q, index) => {
          const questionIndex = allQuestions.findIndex(
            (allQ) => (allQ.id || allQ._id) === (q.id || q._id),
          )
          return {
            ...q,
            displayNumber: questionIndex >= 0 ? questionIndex + 1 : index + 1,
          }
        })
    }

    const handleDeleteQuestion = async (
      idx,
      question,
      setFieldValue,
      currentValues,
      resetForm,
    ) => {
      const questionId = question.id || question._id
      setDeletingQuestionId(questionId)
      if (!question.id || !formState.customFormId) {
        // If no question ID or customFormId, just remove from array
        const newQuestions = [...currentValues.questions]
        newQuestions.splice(idx, 1)
        setFormData({
          questions: newQuestions,
          questCopy: currentValues.questCopy || null,
        })
        return
      }

      try {
        let response = null

        // Call the delete API if question has an ID and form exists
        if (question.id && formState.customFormId) {
          response = await API.AdminAPI.quests.deleteQuestion(
            questId,
            formState.customFormId,
            question.id,
            { isSubQuestion: false },
          )
        }

        // If API call was successful and returned updated form data, use it
        if (response && response.data) {
          // Use the fresh questions data from the API response
          const freshQuestions = response.data.questions || []

          // Process the fresh questions to match our component's format
          const processedQuestions = freshQuestions.map((q) => ({
            id: q._id || q.id,
            text: q.question || '',
            description: q.description || '',
            answerType: q.answerType || 'text',
            questionType: q.questionType || 'direct',
            dependentQuestionId: q.dependentQuestionId || '',
            condition: q.condition || [],
            isRequired: q.isRequired || false,
            originalOptions: Array.isArray(q.options) ? q.options : [],
            subQuestions: q.subQuestions || [],
            imageFileName: q.questionImageName || '',
            position: q.position || '',
            connectedTo: q.connectedTo || '',
            columnNameforReport: q.columnNameforReport || '',
            addressFields: reconstructAddressFields(q),
            isUploading: false,
            isDeleted: q.isDeleted || false,
            scaleOptions: q.scaleOptions,
          }))

          // Filter out deleted questions for display
          const activeQuestions = processedQuestions.filter((q) => !q.isDeleted)

          // Update form data with the fresh data from server
          // Only update Formik form, don't update formData to prevent auto-submission
          if (resetForm) {
            resetForm({
              values: {
                questions: activeQuestions,
                questCopy: currentValues.questCopy || null,
              },
            })
          }
        } else {
          // Fallback: manually remove from array if no API response
          const newQuestions = [...currentValues.questions]
          newQuestions.splice(idx, 1)
          setFormData({
            questions: newQuestions,
            questCopy: currentValues.questCopy || null,
          })
        }

        Notification.showNotification(
          'Question deleted successfully!',
          'success',
        )
      } catch (e) {
        Error.showError(e)
      } finally {
        setDeletingQuestionId(null)
      }
    }

    // Function to handle delete button click — always confirm first
    const handleDeleteClick = (
      idx,
      question,
      setFieldValue,
      currentValues,
      resetForm,
    ) => {
      const questionId = question.id || question._id
      const dependentQuestions = findDependentQuestions(
        questionId,
        currentValues.questions,
      )

      setDeleteModal({
        show: true,
        questionToDelete: {
          idx,
          question,
          setFieldValue,
          currentValues,
          resetForm,
        },
        dependentQuestions,
        isLoading: false,
      })
    }

    // Function to handle modal confirmation
    const handleDeleteConfirm = async () => {
      const { questionToDelete } = deleteModal
      if (!questionToDelete) return

      setDeleteModal((prev) => ({ ...prev, isLoading: true }))

      try {
        const deletedIdx = questionToDelete.idx
        await handleDeleteQuestion(
          questionToDelete.idx,
          questionToDelete.question,
          questionToDelete.setFieldValue,
          questionToDelete.currentValues,
          questionToDelete.resetForm,
        )

        if (
          questionEditor.open &&
          (questionEditor.questionIndex === deletedIdx ||
            questionEditor.insertAfterIndex === deletedIdx)
        ) {
          closeQuestionEditor()
        }

        setDeleteModal({
          show: false,
          questionToDelete: null,
          dependentQuestions: [],
          isLoading: false,
        })
      } catch (error) {
        setDeleteModal((prev) => ({ ...prev, isLoading: false }))
      }
    }

    // Function to handle modal close
    const handleDeleteModalClose = () => {
      setDeleteModal({
        show: false,
        questionToDelete: null,
        dependentQuestions: [],
        isLoading: false,
      })
    }

    const handleFormSubmit = async (values, resetForm) => {
      // Prevent form submission during delete operations
      if (deletingQuestionId) {
        return
      }

      try {
        setFormState((prev) => ({ ...prev, isFormSubmitting: true }))

        // Filter out empty questions
        const questionsForPayload = values.questions.filter((q) => {
          const hasText = q.text && q.text.trim().length > 0
          return hasText
        })

        const payload = {
          ...(formState.customFormId && {
            customFormId: formState.customFormId,
          }),
          questions: questionsForPayload.map((q) =>
            buildQuestionPayload(q, values.questions, isCopyingOperation),
          ),
          type: 'quest',
        }

        const response = await API.AdminAPI.quests.updateCustomForm(
          questId,
          payload,
        )

        // Update customFormId from response if it's a new form
        if (
          response?.data?.customFormId ||
          response?.data?._id ||
          response?.data?.id
        ) {
          const newCustomFormId =
            response.data.customFormId || response.data._id || response.data.id
          setFormState((prev) => ({
            ...prev,
            customFormId: newCustomFormId,
            formExists: true,
          }))
        }

        // Use the fresh questions data from the API response
        const freshQuestions = response.data.questions || []

        // Process the fresh questions to match our component's format
        const processedQuestions = freshQuestions.map((q) => ({
          id: q._id || q.id,
          text: q.question || '',
          description: q.description || '',
          answerType: q.answerType || 'text',
          questionType: q.questionType || 'direct',
          dependentQuestionId: q.dependentQuestionId || '',
          condition: q.condition || [],
          isRequired: q.isRequired || false,
          originalOptions: Array.isArray(q.options) ? q.options : [],
          subQuestions: q.subQuestions || [],
          imageFileName: q.questionImageName || '',
          position: q.position || '',
          connectedTo: q.connectedTo || '',
          columnNameforReport: q.columnNameforReport || '',
          addressFields: reconstructAddressFields(q),
          isUploading: false,
          isDeleted: q.isDeleted || false,
          scaleOptions: q.scaleOptions,
        }))

        // Filter out deleted questions for display
        const activeQuestions = processedQuestions.filter((q) => !q.isDeleted)

        // Reset form with the fresh data from server
        resetForm({
          values: {
            questions: activeQuestions,
            questCopy: values.questCopy || null,
          },
        })

        // Update form data with the fresh questions to reflect the latest saved state
        setFormData({
          questions: activeQuestions,
          questCopy: values.questCopy || null,
        })

        Notification.showNotification(
          'Custom form saved successfully!',
          'success',
        )

        setFormState((prev) => ({ ...prev, isFormSubmitting: false }))
        if (onFormSaved) {
          onFormSaved()
        }
      } catch (e) {
        if (e.code === 'custom_form_already_answered') {
          Notification.showNotification(e.message, 'danger')
        } else {
          Error.showError(e)
        }
        setFormState((prev) => ({ ...prev, isFormSubmitting: false }))
      }
    }

    // Function to handle quest selection and show preview modal
    const handleQuestSelection = async (selectedOption) => {
      if (!selectedOption) return

      const quest = selectedOption.extra
      setShowCopyPicker(false)

      // Set loading state to disable buttons
      setIsCopyingForm(true)

      try {
        // Fetch custom form data from the selected quest
        const result = await fetchAndProcessCustomForm(quest._id)

        // Check if there are any questions to copy (non-deleted questions)
        const questionsToCopy = result.questions.filter((q) => !q.isDeleted)

        // Show preview modal with the questions
        setCopyPreviewModal({
          show: true,
          questions: questionsToCopy,
          selectedQuest: selectedOption,
          pendingCopyData: {
            result,
            selectedOption,
          },
        })
      } catch (e) {
        Error.showError(e)
      } finally {
        // Clear loading state to re-enable buttons
        setIsCopyingForm(false)
      }
    }

    // Function to handle actual copying after preview confirmation
    const handleConfirmCopy = async (setFieldValue) => {
      const { pendingCopyData } = copyPreviewModal
      if (!pendingCopyData) return

      const { result } = pendingCopyData

      // Set loading state to disable buttons
      setIsCopyingForm(true)
      setIsCopyingOperation(true)

      try {
        // Preserve the selected quest in questCopy field
        const updatedFormData = {
          questions: result.questions,
          questCopy: null, // Clear the questCopy field
        }

        // Update the current form with the fetched data
        setFormData(updatedFormData)
        setFormState((prev) => ({ ...prev, shouldResetForm: true }))

        // Save the copied form to get proper IDs
        // Check if there are any questions to copy (non-deleted questions)
        const questionsToCopy = result.questions.filter((q) => !q.isDeleted)
        if (questionsToCopy.length > 0) {
          try {
            const questionsForPayload = result.questions.filter((q) => {
              const hasText = q.text && q.text.trim().length > 0
              return hasText
            })

            const payload = {
              ...(formState.customFormId && {
                customFormId: formState.customFormId,
              }),
              questions: questionsForPayload.map((q) =>
                buildQuestionPayload(q, result.questions, true),
              ),
              type: 'quest',
            }
            const response = await API.AdminAPI.quests.updateCustomForm(
              questId,
              payload,
            )

            // Get the customFormId from response
            const newCustomFormId =
              response?.data?.customFormId ||
              response?.data?._id ||
              response?.data?.id

            if (newCustomFormId) {
              setFormState((prev) => ({
                ...prev,
                customFormId: newCustomFormId,
                formExists: true,
              }))
            }

            const freshQuestions = response.data.questions || []
            const processedQuestions = freshQuestions.map((q) => ({
              id: q._id || q.id,
              text: q.question || '',
              description: q.description || '',
              answerType: q.answerType || 'text',
              questionType: q.questionType || 'direct',
              dependentQuestionId: q.dependentQuestionId || '',
              condition: q.condition || [],
              isRequired: q.isRequired || false,
              originalOptions: Array.isArray(q.options) ? q.options : [],
              subQuestions: q.subQuestions || [],
              imageFileName: q.questionImageName || '',
              position: q.position || '',
              connectedTo: q.connectedTo || '',
              columnNameforReport: q.columnNameforReport || '',
              addressFields: reconstructAddressFields(q),
              isUploading: false,
              isDeleted: q.isDeleted || false,
              scaleOptions: q.scaleOptions,
            }))

            // Filter out deleted questions for display
            const activeQuestions = processedQuestions.filter(
              (q) => !q.isDeleted,
            )

            // Update formData with the fresh data from server
            const updatedFormData = {
              questions: activeQuestions,
              questCopy: null,
            }

            setFormData(updatedFormData)

            // Force a form reset with the fresh data to ensure synchronization
            setFormState((prev) => ({
              ...prev,
              shouldResetForm: true,
              customFormId: newCustomFormId || prev.customFormId,
              formExists: true,
            }))

            Notification.showNotification(
              'Questions copied and saved successfully!',
              'success',
            )
          } catch (saveError) {
            Error.showError(saveError)
          }
        } else {
          // No questions to copy from the selected quest
          Notification.showNotification(
            'The selected quest has no questions to copy.',
            'warning',
          )
        }
      } catch (e) {
        Error.showError(e)
      } finally {
        // Clear loading state to re-enable buttons
        setIsCopyingForm(false)
      }
    }

    // Function to close copy preview modal
    const handleCloseCopyPreviewModal = () => {
      setCopyPreviewModal({
        show: false,
        questions: [],
        selectedQuest: null,
        pendingCopyData: null,
      })
    }

    // Function to handle modal cancellation (clear the field)
    const handleCopyModalCancel = (setFieldValue) => {
      handleCloseCopyPreviewModal()
      // Clear the questCopy field when modal is cancelled
      setFieldValue('questCopy', null)
    }

    // Function to handle question card click (open inline editor under row)
    const handleQuestionEdit = (question, index) => {
      setQuestionEditor({
        open: true,
        question: question,
        questionIndex: index,
        saveMode: 'append',
        insertAfterIndex: null,
      })
    }

    // Function to close inline question editor
    const closeQuestionEditor = () => {
      setQuestionEditor({
        open: false,
        question: null,
        questionIndex: null,
        saveMode: 'append',
        insertAfterIndex: null,
      })
      setValidationErrors({})
    }

    // Function to handle adding a new question - opens blank add-form at bottom
    const handleAddQuestion = () => {
      const freshQuestion = createFreshQuestion()

      setQuestionEditor({
        open: true,
        question: freshQuestion,
        questionIndex: -1,
        saveMode: 'append',
        insertAfterIndex: null,
      })
    }

    // Function to handle adding a new question below a selected question
    const handleAddQuestionBelow = (selectedQuestionIndex) => {
      const freshQuestion = createFreshQuestion()

      setQuestionEditor({
        open: true,
        question: freshQuestion,
        questionIndex: -1,
        saveMode: 'insertBelow',
        insertAfterIndex: selectedQuestionIndex,
      })
    }

    const handleFormResetDone = React.useCallback(() => {
      setFormState((prev) => ({ ...prev, shouldResetForm: false }))
    }, [])

    const resolveEditorQuestionIndex = (totalQuestions) => {
      if (questionEditor.questionIndex === -1) {
        if (
          questionEditor.saveMode === 'insertBelow' &&
          questionEditor.insertAfterIndex !== null
        ) {
          return questionEditor.insertAfterIndex + 1
        }
        return totalQuestions
      }
      return questionEditor.questionIndex
    }

    const ensureImageRef = (idx) => {
      if (!imageRefs.current[idx]) {
        imageRefs.current[idx] = React.createRef()
      }
    }

    const isRowEditing = (actualIdx) => {
      if (!questionEditor.open) return false
      return questionEditor.questionIndex === actualIdx
    }

    const shouldShowEditorAfter = (actualIdx) => {
      if (!questionEditor.open || !questionEditor.question) return false
      if (questionEditor.questionIndex === actualIdx) return true
      // Insert-below: show the standalone add form after this row (do not
      // mark the source row as is-editing — that changes add UI into edit UI).
      return (
        questionEditor.saveMode === 'insertBelow' &&
        questionEditor.questionIndex === -1 &&
        questionEditor.insertAfterIndex === actualIdx
      )
    }

    const isAddingAtBottom =
      questionEditor.open &&
      questionEditor.questionIndex === -1 &&
      questionEditor.saveMode === 'append'

    const getParentQuestionsForEditor = (allQuestions) => {
      if (!questionEditor.question) return []

      const insertBoundaryIndex =
        questionEditor.saveMode === 'insertBelow' &&
        questionEditor.questionIndex === -1 &&
        questionEditor.insertAfterIndex !== null
          ? questionEditor.insertAfterIndex + 1
          : questionEditor.questionIndex

      return allQuestions.filter((q, idx) => {
        if (insertBoundaryIndex !== -1 && idx >= insertBoundaryIndex) {
          return false
        }
        return (
          q.text &&
          q.text.trim() &&
          q.questionType !== 'dependent' &&
          (q.answerType === 'singleSelectionDropdown' ||
            q.answerType === 'multipleSelectionDropdown')
        )
      })
    }

    const handleQuestionEditorSave = (
      processedQuestions,
      customFormId,
      setFieldValue,
    ) => {
      if (customFormId) {
        setFormState((prev) => ({
          ...prev,
          customFormId: customFormId,
          formExists: true,
        }))
      }

      if (processedQuestions && processedQuestions.length > 0) {
        const activeQuestions = processedQuestions.filter((q) => !q.isDeleted)
        setFieldValue('questions', activeQuestions)
        setFormData((prev) => ({
          ...prev,
          questions: activeQuestions,
        }))
      }

      closeQuestionEditor()
    }

    const renderInlineQuestionEditor = (values, setFieldValue) => {
      if (!questionEditor.open || !questionEditor.question) return null

      const resolvedQuestionIndex = resolveEditorQuestionIndex(
        values.questions.length,
      )
      ensureImageRef(resolvedQuestionIndex)

      return (
        <div className='experience-add-form'>
          <QuestionEditor
            key={
              questionEditor.questionIndex === -1
                ? `new-${questionEditor.saveMode}-${questionEditor.insertAfterIndex}`
                : `edit-${questionEditor.questionIndex}-${
                    questionEditor.question.id ||
                    questionEditor.question._id ||
                    'draft'
                  }`
            }
            question={questionEditor.question}
            actualIdx={resolvedQuestionIndex}
            displayIdx={resolvedQuestionIndex}
            formState={formState}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            onDelete={() => {}}
            onMove={() => {}}
            onUpdate={() => {}}
            questId={questId}
            imageRefs={imageRefs}
            allQuestions={getParentQuestionsForEditor(values.questions)}
            allQuestionsForCondition={values.questions}
            allQuestionsForParent={values.questions}
            totalQuestions={values.questions.length}
            hideActionButtons={true}
            saveMode={questionEditor.saveMode}
            insertAfterIndex={questionEditor.insertAfterIndex}
            existingQuestions={values.questions}
            onSave={(processedQuestions, customFormId) =>
              handleQuestionEditorSave(
                processedQuestions,
                customFormId,
                setFieldValue,
              )
            }
            onCancel={closeQuestionEditor}
            isSaving={formState.isFormSubmitting}
          />
        </div>
      )
    }

    if (formState.isLoading) {
      return <ABSpinner />
    }

    return (
      <Formik
        key={`${formState.customFormId}-${formData.questions.length}`}
        initialValues={formData}
        enableReinitialize
        onSubmit={(values, { resetForm }) =>
          handleFormSubmit(values, resetForm)
        }>
        {({ setFieldValue, values, resetForm, submitForm }) => (
          <div className='main-video-form'>
            <FormResetWatcher
              shouldResetForm={formState.shouldResetForm}
              formData={formData}
              resetForm={resetForm}
              onResetDone={handleFormResetDone}
            />
            <QuestionsSyncWatcher
              questions={values.questions}
              onQuestionsChange={onQuestionsChange}
            />

            <FieldArray name='questions'>
              {({ push, remove, move }) => {
                const moveQuestion = (from, to) => {
                  if (from === to || from < 0 || to < 0) return
                  move(from, to)
                }
                const persistAfterReorder = () => {
                  window.setTimeout(() => {
                    submitForm()
                  }, 0)
                }
                return (
                  <>
                    {values.questions.length === 0 && !questionEditor.open ? (
                      <div className='empty-state'>
                        <div className='empty-title text-center'>
                          <h5>No questions created yet</h5>
                        </div>
                        <div className='experience-form-actions justify-content-center'>
                          <button
                            type='button'
                            className='btn-navy experience-btn-sm'
                            onClick={handleAddQuestion}
                            disabled={isCopyingForm}>
                            Add question
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className='questions-list' data-add-list='enrol-q'>
                        {values.questions.map((question, actualIdx) => {
                          if (question.isDeleted) {
                            return null
                          }

                          const displayIdx =
                            values.questions.filter(
                              (q, idx) => idx <= actualIdx && !q.isDeleted,
                            ).length - 1

                          ensureImageRef(actualIdx)

                          return (
                            <Fragment
                              key={question.id || question._id || actualIdx}>
                              <QuestionCard
                                question={question}
                                index={displayIdx}
                                actualIndex={actualIdx}
                                isEditing={isRowEditing(actualIdx)}
                                onEdit={() =>
                                  handleQuestionEdit(question, actualIdx)
                                }
                                onAddBelow={() => {
                                  handleAddQuestionBelow(actualIdx)
                                }}
                                moveItem={moveQuestion}
                                onDragEnd={persistAfterReorder}
                                onDelete={() => {
                                  handleDeleteClick(
                                    actualIdx,
                                    question,
                                    setFieldValue,
                                    values,
                                    resetForm,
                                  )
                                }}
                                isUploading={
                                  question.isUploading ||
                                  deletingQuestionId ===
                                    (question.id || question._id)
                                }
                                allQuestions={values.questions}
                              />
                              {shouldShowEditorAfter(actualIdx)
                                ? renderInlineQuestionEditor(
                                    values,
                                    setFieldValue,
                                  )
                                : null}
                            </Fragment>
                          )
                        })}

                        {isAddingAtBottom
                          ? renderInlineQuestionEditor(values, setFieldValue)
                          : null}

                        {!questionEditor.open ? (
                          <div className='experience-form-actions'>
                            <button
                              type='button'
                              className='btn-navy experience-btn-sm'
                              onClick={handleAddQuestion}
                              disabled={isCopyingForm}>
                              Add question
                            </button>
                            {!hidePreviewButton ? (
                              <button
                                type='button'
                                className='btn-outline-experience experience-btn-sm'
                                disabled={isPreviewDisabled}
                                onClick={() => {
                                  if (isPreviewDisabled || !onPreviewClick)
                                    return
                                  onPreviewClick()
                                }}>
                                Preview
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </>
                )
              }}
            </FieldArray>

            <ExperienceConfirmModal
              show={deleteModal.show}
              onHide={handleDeleteModalClose}
              onConfirm={handleDeleteConfirm}
              title='Delete this question?'
              message={
                deleteModal.dependentQuestions?.length > 0
                  ? 'Deleting this question will also remove all its dependent questions. This cannot be undone.'
                  : 'This question will be removed from the enrollment form. This cannot be undone.'
              }
              confirmLabel='Delete'
              loadingLabel='Deleting…'
              isLoading={deleteModal.isLoading}
            />

            {/* Question Copy Preview Modal */}
            <QuestionCopyPreviewModal
              showModal={copyPreviewModal.show}
              setShowModal={handleCloseCopyPreviewModal}
              onCancel={() => handleCopyModalCancel(setFieldValue)}
              onConfirm={() => handleConfirmCopy(setFieldValue)}
              questions={copyPreviewModal.questions}
              selectedQuest={copyPreviewModal.selectedQuest}
              isLoading={isCopyingForm}
            />

            <ExperienceCopyModal
              show={showCopyPicker}
              onHide={() => {
                if (!isCopyingForm) setShowCopyPicker(false)
              }}
              onSelect={handleQuestSelection}
              currentQuestId={questId}
              title='Copy enrollment form'
              lead='Copy questions from another experience. Existing answers on this form are replaced when you confirm.'
              isLoadingSelection={isCopyingForm}
            />
          </div>
        )}
      </Formik>
    )
  },
)

QuestTypeformEditor.displayName = 'QuestTypeformEditor'

export default QuestTypeformEditor
